import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { mainActions } from "system/store/slices/main-slice";
import useApi from "./use-api";
import useConfiguration from "./use-configuration";

// Loads totalReporting + licensesInUse for a given date. Cached by siteId + date,
// so pages sharing the same selection reuse the fetch.
const useEndpointCounts = (selectedDateString: string | null) => {
  const api = useApi();
  const dispatch = useDispatch();
  const { currentTenant } = useConfiguration();
  const endpointCounts = useSelector((state: IRootState) => state.main.endpointCounts);

  useEffect(() => {
    if (!currentTenant?.siteId || !selectedDateString) return;

    const cacheKey = `${currentTenant.siteId}_${selectedDateString}`;
    if (endpointCounts?.cacheKey === cacheKey) return;

    dispatch(mainActions.setEndpointCounts(null));

    const controller = new AbortController();
    api.summary
      .endpointCounts(new Date(selectedDateString), {
        signal: controller.signal,
        retries: 3,
        baseDelay: 1000,
        maxDelay: 10000,
      })
      .then((data) => {
        if (data?.isSuccess) {
          dispatch(
            mainActions.setEndpointCounts({
              totalReporting: data.totalReporting,
              licensesInUse: data.licensesInUse,
              cacheKey,
            })
          );
        }
      })
      .catch((error: any) => {
        if (error?.name === "AbortError") return;
        console.error("Error fetching endpoint counts:", error);
      });

    return () => controller.abort();
  }, [currentTenant?.siteId, selectedDateString, endpointCounts?.cacheKey, api.summary, dispatch]);
};

export default useEndpointCounts;
